import React, { useState, useEffect } from 'react';
import api, { creditorsAPI } from '../services/api';
import { formatApiError } from '../utils/errorMessage';
import { isPaymentUnconfirmed, PAYMENT_UNCONFIRMED, BILL_REPLAYED } from '../utils/idempotencyKey';
import { todayLocal, parseCalendarDay } from '../utils/localDate';
import { BILL_FREQUENCY_OPTIONS, INTERVAL_UNIT_OPTIONS, billFrequencyLabel } from '../utils/billFrequency';
import { parseMoneyInput, MONEY_FIELD_ERROR } from '../utils/money';
import ErrorBoundary from '../components/ErrorBoundary';
const RecurringBills = ({ selectedBusiness }) => {
  const [bills, setBills] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [creditors, setCreditors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showQuickAddCreditor, setShowQuickAddCreditor] = useState(false);
  const [newCreditorName, setNewCreditorName] = useState('');
  const [editingBill, setEditingBill] = useState(null);
  // Frequency picked in the open form (null = the bill's saved frequency).
  const [frequencyChoice, setFrequencyChoice] = useState(null);
  const [historyBill, setHistoryBill] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState('');
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedBusiness]);

  const loadData = async () => {
    try {
      setError(null);
      const params = selectedBusiness ? { business_id: selectedBusiness } : {};
      
      // Only fetch creditors for the selected business + global creditors
      const creditorParams = { is_active: true };
      if (selectedBusiness) {
        creditorParams.business_id = selectedBusiness;
      }
      
      const [billsRes, businessesRes, accountsRes, categoriesRes, creditorsRes] = await Promise.all([
        api.get('/recurring-bills', { params }),
        api.get('/businesses').catch(() => ({ data: [] })),
        api.get('/accounts', { params }).catch(() => ({ data: [] })),
        api.get('/categories').catch(() => ({ data: [] })),
        creditorsAPI.getAll(creditorParams).catch(() => ({ data: [] }))
      ]);

      setBills(billsRes.data || []);
      setBusinesses(businessesRes.data || []);
      setAccounts(accountsRes.data || []);
      setCategories(categoriesRes.data || []);
      setCreditors(creditorsRes.data || []);
      
      // Calculate stats
      calculateStats(billsRes.data || []);
    } catch (err) {
      console.error('Failed to load recurring bills:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      setError(formatApiError(err, 'Failed to load recurring bills'));
    }
  };

  const calculateStats = (billsData) => {
    const active = billsData.filter(b => b.is_active);
    const totalMonthly = billsData[0]?.total_monthly || 0;
    const totalAnnual = billsData[0]?.total_annual || 0;

    const dueSoon = active.filter(b => {
      if (!b.next_due_date) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = parseCalendarDay(b.next_due_date);
      due.setHours(0, 0, 0, 0);
      const daysUntil = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
      return daysUntil >= 0 && daysUntil <= 7;
    }).length;

    const overdue = active.filter(b => {
      if (!b.next_due_date) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = parseCalendarDay(b.next_due_date);
      due.setHours(0, 0, 0, 0);
      return due < today;
    }).length;

    setStats({
      total: billsData.length,
      active: active.length,
      totalMonthly,
      totalAnnual,
      dueSoon,
      overdue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    if (data.frequency !== 'custom') {
      delete data.interval_count;
      delete data.interval_unit;
    }
    const amount = parseMoneyInput(data.amount);
    if (amount.error || amount.value === null) {
      setError(`Amount: ${MONEY_FIELD_ERROR}`);
      return;
    }
    
    try {
      setError(null);
      
      // Convert empty strings to null and format data
      const dataToSubmit = {
        ...data,
        business_id: data.business_id || null,
        account_id: data.account_id || null,
        category_id: data.category_id || null,
        creditor_id: data.creditor_id || null,
        due_day: data.due_day || null,
        notes: data.notes || null,
        amount: amount.value,
        reminder_days_before: parseInt(data.reminder_days_before) || 3,
        is_auto_pay: data.is_auto_pay === 'on',
        is_active: data.is_active === 'on'
      };

      if (editingBill) {
        await api.put(`/recurring-bills/${editingBill.id}`, dataToSubmit);
      } else {
        await api.post('/recurring-bills', dataToSubmit);
      }
      
      setShowModal(false); setFrequencyChoice(null);
      setEditingBill(null);
      loadData();
    } catch (err) {
      console.error('Failed to save bill:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      setError(formatApiError(err, 'Failed to save recurring bill'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recurring bill?')) {
      return;
    }

    try {
      setError(null);
      await api.delete(`/recurring-bills/${id}`);
      loadData();
    } catch (err) {
      console.error('Failed to delete bill:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      setError(formatApiError(err, 'Failed to delete recurring bill'));
    }
  };

  const openPaymentHistory = async (bill) => {
    try {
      setError(null);
      setHistoryBill(bill);
      setPaymentHistory(null);
      const response = await api.get(`/recurring-bills/${bill.id}/payments`);
      setPaymentHistory(response.data);
    } catch (err) {
      console.error('Failed to load payment history:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      setError(formatApiError(err, 'Failed to load payment history'));
      setHistoryBill(null);
    }
  };

  const handlePay = async (bill) => {
    if (payingId) return;
    setPayingId(bill.id);
    try {
      setError(null);
      setNotice('');
      const response = await api.post(`/recurring-bills/${bill.id}/pay`, {
        payment_date: todayLocal(),
        expected_due_date: bill.next_due_date_key
      });
      if (response.data?.replayed) setNotice(BILL_REPLAYED);
      loadData();
    } catch (err) {
      console.error('Failed to mark bill as paid:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      if (isPaymentUnconfirmed(err)) setError(PAYMENT_UNCONFIRMED);
      else setError(formatApiError(err, 'Failed to mark bill as paid'));
    } finally {
      setPayingId(null);
    }
  };

  const handleQuickAddCreditor = async (e) => {
    e.preventDefault();
    if (!newCreditorName.trim()) return;
    
    try {
      setError(null);
      const response = await creditorsAPI.create({
        creditor_name: newCreditorName.trim(),
        creditor_type: 'vendor',
        is_active: true
      });
      
      // Reload creditors
      const creditorsRes = await creditorsAPI.getAll({ is_active: true });
      setCreditors(creditorsRes.data || []);
      
      // Set the newly created creditor as selected
      const creditorSelect = document.querySelector('select[name="creditor_id"]');
      if (creditorSelect) {
        creditorSelect.value = response.data.id;
      }
      
      setShowQuickAddCreditor(false);
      setNewCreditorName('');
    } catch (err) {
      console.error('Failed to create creditor:', err?.response?.status || err?.code || 'no response', err?.response?.data?.code || '');
      setError(formatApiError(err, 'Failed to create creditor'));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return parseCalendarDay(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = parseCalendarDay(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDueDateBadge = (dueDate) => {
    const daysUntil = getDaysUntilDue(dueDate);
    if (daysUntil === null) return null;
    
    if (daysUntil < 0) {
      return (
        <span className="text-xs text-red-600 font-semibold">
          {Math.abs(daysUntil)} days overdue
        </span>
      );
    } else if (daysUntil === 0) {
      return (
        <span className="text-xs text-red-600 font-semibold">
          Due today
        </span>
      );
    } else if (daysUntil <= 7) {
      return (
        <span className="text-xs text-orange-600">
          Due in {daysUntil} {daysUntil === 1 ? 'day' : 'days'}
        </span>
      );
    } else {
      return (
        <span className="text-xs text-gray-500">
          Due in {daysUntil} days
        </span>
      );
    }
  };

  return (
    <ErrorBoundary section="Recurring Bills">
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Recurring Bills</h2>
          <p className="text-gray-600 mt-1">Manage your recurring payment obligations</p>
        </div>
        <button
          onClick={() => { setShowModal(true); setFrequencyChoice(null); setEditingBill(null); }}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition w-full sm:w-auto"
        >
          + Add Bill
        </button>
      </div>

      {/* Error Display */}
      {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="flex items-center justify-between">
              <p className="text-red-800">{formatApiError(error, 'Something went wrong')}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800"
              >
                ✕
              </button>
            </div>
          </div>
      )}

      {notice && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800" role="status">
          {notice}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <p className="text-sm text-gray-600">Total Bills</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <p className="text-sm text-gray-600">Active Bills</p>
            <p className="text-2xl sm:text-3xl font-bold text-green-600">{stats.active}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <p className="text-sm text-gray-600">Estimated monthly</p>
            <p className="text-xl sm:text-2xl font-bold text-blue-600">{formatCurrency(stats.totalMonthly)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <p className="text-sm text-gray-600">Estimated annual</p>
            <p className="text-xl sm:text-2xl font-bold text-purple-600">{formatCurrency(stats.totalAnnual)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <p className="text-sm text-gray-600">Due Soon (7d)</p>
            <p className="text-2xl sm:text-3xl font-bold text-orange-600">{stats.dueSoon}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <p className="text-sm text-gray-600">Overdue</p>
            <p className="text-2xl sm:text-3xl font-bold text-red-600">{stats.overdue}</p>
          </div>
        </div>
      )}

      {/* Bills Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill / Payee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Business</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Due</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Last Paid</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Payment</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bills.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center">
                    <p className="text-gray-500 mb-4">No recurring bills found</p>
                    <button
                      onClick={() => setShowModal(true)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      + Add your first bill
                    </button>
                  </td>
                </tr>
              ) : (
                bills.map((bill) => (
                  <tr key={bill.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{bill.bill_name}</p>
                        <p className="text-xs text-gray-500">{bill.payee}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 hidden sm:table-cell">
                      {bill.business_name || 'Personal'}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                        {billFrequencyLabel(bill)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-semibold">
                      {formatCurrency(bill.amount)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{formatDate(bill.next_due_date)}</div>
                      {getDueDateBadge(bill.next_due_date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 hidden md:table-cell">
                      {formatDate(bill.last_paid_date)}
                    </td>
                    <td className="px-6 py-4 text-center hidden lg:table-cell">
                      {bill.is_auto_pay ? (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Auto
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          Manual
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        bill.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {bill.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm whitespace-nowrap">
                      <button
                        onClick={() => openPaymentHistory(bill)}
                        className="text-gray-700 hover:text-gray-900 mr-3"
                      >
                        History
                      </button>
                      <button
                        onClick={() => handlePay(bill)}
                        disabled={!bill.is_active || payingId !== null}
                        className="text-green-600 hover:text-green-800 mr-3 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Mark as paid"
                      >
                        {payingId === bill.id ? 'Saving...' : 'Pay'}
                      </button>
                      <button
                        onClick={() => { setEditingBill(bill); setShowModal(true); setFrequencyChoice(null); }}
                        className="text-blue-600 hover:text-blue-800 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(bill.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {historyBill && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">{historyBill.bill_name} payments</h2>
              <button
                onClick={() => { setHistoryBill(null); setPaymentHistory(null); }}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Last paid: {formatDate(paymentHistory ? paymentHistory.last_paid_date : historyBill.last_paid_date)}
            </p>
            {!paymentHistory ? (
              <p className="text-sm text-gray-500">Loading payments...</p>
            ) : paymentHistory.payments.length === 0 ? (
              <p className="text-sm text-gray-500">No payments recorded for this bill.</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paymentHistory.payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatDate(payment.transaction_date)}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">{payment.description}</td>
                      <td className="px-3 py-2 text-sm text-right font-medium">{formatCurrency(payment.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              {editingBill ? 'Edit' : 'Add'} Recurring Bill
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input 
                type="hidden" 
                name="business_id" 
                value={selectedBusiness || editingBill?.business_id || ''} 
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Bill Name */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Bill Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="bill_name"
                    defaultValue={editingBill?.bill_name || ''}
                    required
                    className="w-full border rounded px-3 py-2"
                  />
                </div>

                {/* Creditor/Payee */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Creditor/Payee <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="creditor_id"
                      defaultValue={editingBill?.creditor_id || ''}
                      className="flex-1 border rounded px-3 py-2"
                      onChange={(e) => {
                        if (e.target.value === 'add_new') {
                          setShowQuickAddCreditor(true);
                          e.target.value = '';
                        }
                      }}
                    >
                      <option value="">Select Creditor</option>
                      {creditors.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.creditor_name}{c.creditor_type ? ` (${c.creditor_type.replace(/_/g, ' ')})` : ''}
                        </option>
                      ))}
                      <option value="add_new" className="text-blue-600 font-semibold">+ Add New Creditor</option>
                    </select>
                  </div>
                  <input
                    type="text"
                    name="payee"
                    defaultValue={editingBill?.payee || ''}
                    placeholder="Or enter payee name manually"
                    className="w-full border rounded px-3 py-2 mt-2 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Select from creditors list or enter manually</p>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="amount"
                    defaultValue={editingBill?.amount || ''}
                    required
                    className="w-full border rounded px-3 py-2"
                  />
                </div>

                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium mb-1">Frequency</label>
                  <select
                    name="frequency"
                    defaultValue={editingBill?.frequency === 'annual' ? 'yearly' : (editingBill?.frequency || 'monthly')}
                    onChange={(e) => setFrequencyChoice(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                  >
                    {BILL_FREQUENCY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {(frequencyChoice ?? editingBill?.frequency) === 'custom' && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-sm">Every</span>
                      <input
                        type="number"
                        name="interval_count"
                        min="1"
                        max="366"
                        step="1"
                        required
                        defaultValue={editingBill?.interval_count || ''}
                        className="w-24 border rounded px-3 py-2"
                        aria-label="Interval count"
                      />
                      <select
                        name="interval_unit"
                        defaultValue={editingBill?.interval_unit || 'month'}
                        className="border rounded px-3 py-2"
                        aria-label="Interval unit"
                      >
                        {INTERVAL_UNIT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Next Due Date */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Next Due Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="next_due_date"
                    defaultValue={editingBill?.next_due_date ? editingBill.next_due_date.split('T')[0] : ''}
                    required
                    className="w-full border rounded px-3 py-2"
                  />
                </div>

                {/* Due Day */}
                <div>
                  <label className="block text-sm font-medium mb-1">Due Day (1-31)</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    name="due_day"
                    defaultValue={editingBill?.due_day || ''}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>

                {/* Account */}
                <div>
                  <label className="block text-sm font-medium mb-1">Account</label>
                  <select
                    name="account_id"
                    defaultValue={editingBill?.account_id || ''}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="">Select Account</option>
                    {accounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.account_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    name="category_id"
                    defaultValue={editingBill?.category_id || ''}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reminder Days */}
                <div>
                  <label className="block text-sm font-medium mb-1">Reminder Days Before</label>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    name="reminder_days_before"
                    defaultValue={editingBill?.reminder_days_before || '3'}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_auto_pay"
                    defaultChecked={editingBill?.is_auto_pay || false}
                    className="mr-2"
                  />
                  <span className="text-sm">Auto Pay</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    defaultChecked={editingBill?.is_active !== false}
                    className="mr-2"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  name="notes"
                  defaultValue={editingBill?.notes || ''}
                  rows="3"
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              {/* Form Actions */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setFrequencyChoice(null); setEditingBill(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto"
                >
                  {editingBill ? 'Update' : 'Add'} Bill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Add Creditor Modal */}
      {showQuickAddCreditor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Quick Add Creditor</h3>
            <form onSubmit={handleQuickAddCreditor}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Creditor Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newCreditorName}
                  onChange={(e) => setNewCreditorName(e.target.value)}
                  placeholder="e.g., Electric Company"
                  className="w-full border rounded px-3 py-2"
                  autoFocus
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  You can add more details later in Creditors page
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowQuickAddCreditor(false);
                    setNewCreditorName('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Creditor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
};

export default RecurringBills;